import _slicedToArray from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import { t } from "i18next";
var EM_DASH = "—";
var MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
var WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Pads a number to a two-digit string. */
function pad(value) {
  return value < 10 ? "0".concat(value) : "".concat(value);
}

/** Returns the short month name for a zero-based month index. */
function monthName(index) {
  var _MONTHS$index;
  return (_MONTHS$index = MONTHS[index]) !== null && _MONTHS$index !== void 0 ? _MONTHS$index : "";
}

/** Returns the short weekday name for a zero-based day index. */
function weekdayName(index) {
  var _WEEKDAYS$index;
  return (_WEEKDAYS$index = WEEKDAYS[index]) !== null && _WEEKDAYS$index !== void 0 ? _WEEKDAYS$index : "";
}

/** Parses an ISO string into a valid Date, or null when missing or invalid. */
function parseDate(iso) {
  if (!iso) return null;
  var date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Formats an ISO date as 'MMM D, YYYY', e.g. 'Jun 23, 2026', or an em dash when missing or invalid. */
export function formatShortDate(iso) {
  var date = parseDate(iso);
  if (!date) return EM_DASH;
  return "".concat(monthName(date.getMonth()), " ").concat(date.getDate(), ", ").concat(date.getFullYear());
}

/** Formats an ISO date as MM/DD/YYYY, or an em dash when missing or invalid. */
export function formatDate(iso) {
  if (!iso) return EM_DASH;
  var date = new Date(iso);
  if (Number.isNaN(date.getTime())) return EM_DASH;
  return "".concat(pad(date.getMonth() + 1), "/").concat(pad(date.getDate()), "/").concat(date.getFullYear());
}

/**
 * Formats an EMR date-of-birth string (YYYY-MM-DD / YYYY/MM/DD / YYYYMMDD) without
 * timezone shifting. When a `format` token string is supplied (e.g. UI settings
 * `dob_format`, using `dd`/`mm`/`yyyy` placeholders) the date is rendered in that
 * format; otherwise it falls back to MM/DD/YYYY. Returns an empty string when the
 * value is missing or unparseable.
 */
export function formatDob(iso, format) {
  if (!iso) return "";
  var match = /^(\d{4})[-/]?(\d{2})[-/]?(\d{2})/.exec(iso.trim());
  if (!match) return "";
  var _match = _slicedToArray(match, 4),
    _match$ = _match[1],
    year = _match$ === void 0 ? "" : _match$,
    _match$2 = _match[2],
    month = _match$2 === void 0 ? "" : _match$2,
    _match$3 = _match[3],
    day = _match$3 === void 0 ? "" : _match$3;
  if (typeof format === "string" && format.trim() !== "") {
    return format.trim().toLowerCase().replace("dd", day).replace("mm", month).replace("yyyy", year);
  }
  return "".concat(month, "/").concat(day, "/").concat(year);
}

/** Returns whole-year age from an ISO birth date, or null when unparseable. */
export function calculateAge(iso) {
  if (!iso) return null;
  var dob = new Date(iso);
  if (Number.isNaN(dob.getTime())) return null;
  var now = new Date();
  var age = now.getFullYear() - dob.getFullYear();
  var monthDiff = now.getMonth() - dob.getMonth();
  if (monthDiff < 0 || monthDiff === 0 && now.getDate() < dob.getDate()) age -= 1;
  return age;
}

/** Formats an ISO datetime as 'MMM DD, YYYY · HH:mm', or an em dash when missing. */
export function formatDateTime(iso) {
  var date = parseDate(iso);
  if (!date) return EM_DASH;
  return "".concat(monthName(date.getMonth()), " ").concat(pad(date.getDate()), ", ").concat(date.getFullYear(), " \xB7 ").concat(pad(date.getHours()), ":").concat(pad(date.getMinutes()));
}

/** Returns the uppercase three-letter month for an ISO date, e.g. 'JUN'. */
export function formatMonthShort(iso) {
  var date = parseDate(iso);
  return date ? monthName(date.getMonth()).toUpperCase() : EM_DASH;
}

/** Returns the day of month for an ISO date, e.g. '13'. */
export function formatDayOfMonth(iso) {
  var date = parseDate(iso);
  return date ? "".concat(date.getDate()) : EM_DASH;
}

/** Formats an ISO date as 'MMM D', e.g. 'May 28', or an em dash when missing. */
export function formatMonthDay(iso) {
  var date = parseDate(iso);
  return date ? "".concat(monthName(date.getMonth()), " ").concat(date.getDate()) : EM_DASH;
}

/** Formats an ISO date as 'MMM D, YYYY', e.g. 'Jun 19, 2026', or an em dash when missing. */
export function formatMonthDayYear(iso) {
  var date = parseDate(iso);
  return date ? "".concat(monthName(date.getMonth()), " ").concat(date.getDate(), ", ").concat(date.getFullYear()) : EM_DASH;
}

/** Returns the file name from a path or S3 key, dropping any directory prefix. */
export function formatFileName(pathOrKey) {
  if (!pathOrKey) return "";
  var segments = pathOrKey.split(/[\\/]/);
  return segments[segments.length - 1] || pathOrKey;
}

/** Formats an ISO datetime as a 12-hour clock time, e.g. '2:30 PM'. */
export function formatTime(iso) {
  var date = parseDate(iso);
  if (!date) return EM_DASH;
  var period = date.getHours() >= 12 ? "PM" : "AM";
  var hours = date.getHours() % 12 || 12;
  return "".concat(hours, ":").concat(pad(date.getMinutes()), " ").concat(period);
}

/** Returns the short weekday for an ISO date, e.g. 'Fri'. */
export function formatWeekday(iso) {
  var date = parseDate(iso);
  return date ? weekdayName(date.getDay()) : EM_DASH;
}

/** Formats an ISO timestamp as '2:30 pm, 15 Jun 2026', as used in linked-record row labels. */
export function formatTimestamp(iso) {
  var date = parseDate(iso);
  if (!date) return "";
  var period = date.getHours() >= 12 ? "pm" : "am";
  var hours = date.getHours() % 12 || 12;
  return "".concat(hours, ":").concat(pad(date.getMinutes()), " ").concat(period, ", ").concat(date.getDate(), " ").concat(monthName(date.getMonth()), " ").concat(date.getFullYear());
}

/** Formats an ISO date as 'D MMM YYYY', e.g. '23 Jun 2026', or an empty string when missing. */
export function formatDayMonthYear(iso) {
  var date = parseDate(iso);
  if (!date) return "";
  return "".concat(date.getDate(), " ").concat(monthName(date.getMonth()), " ").concat(date.getFullYear());
}

/** Returns true when two dates fall on the same calendar day. */
function isSameCalendarDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** Formats an ISO timestamp as 'h:mm am, MMM D', collapsing today to a relative label. */
export function formatStripDateTime(iso) {
  var date = parseDate(iso);
  if (!date) return "";
  var h = date.getHours() % 12 || 12;
  var period = date.getHours() >= 12 ? "pm" : "am";
  var time = "".concat(h, ":").concat(pad(date.getMinutes()), " ").concat(period);
  var now = new Date();
  if (isSameCalendarDay(date, now)) return "".concat(time, ", ").concat(t("today", {
    defaultValue: "Today"
  }));
  return "".concat(time, ", ").concat(monthName(date.getMonth()), " ").concat(date.getDate());
}

/** Returns true when a value is non-empty — non-blank string or non-empty array. */
export function hasValue(value) {
  return Array.isArray(value) ? value.length > 0 : !!value && value.trim() !== "";
}

/** Converts a snake_case key to a Title Case display label, e.g. 'patient_class' → 'Patient Class'. */
export function snakeToTitle(key) {
  return key.split("_").map(function (w) {
    return w.charAt(0).toUpperCase() + w.slice(1);
  }).join(" ");
}

/** Formats a past epoch timestamp as a coarse relative phrase, e.g. '5 days ago'. */
export function formatRelativeTime(timestamp) {
  if (!timestamp) return EM_DASH;
  var seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (seconds < 60) return "just now";
  var units = [[86400, "day"], [3600, "hour"], [60, "minute"]];
  for (var _i = 0, _units = units; _i < _units.length; _i++) {
    var _units$_i = _slicedToArray(_units[_i], 2),
      unitSeconds = _units$_i[0],
      label = _units$_i[1];
    var amount = Math.floor(seconds / unitSeconds);
    if (amount >= 1) return "".concat(amount, " ").concat(label).concat(amount > 1 ? "s" : "", " ago");
  }
  return "just now";
}